import { z } from "zod";
import { nookEmbedSchema } from "./embed.js";
/**
 * The nook contract (block 03), shared by `apps/backend` and
 * `@nook/api-client`. camelCase throughout — `nextCursor`, never
 * `next_cursor` (decision register D6; see `user.ts` for the argument).
 */
/**
 * Which client produced a capture. **Provenance only.** It is recorded on the
 * row and never consulted while processing one, which is what keeps a
 * WhatsApp-captured nook and a webapp-captured nook the same nook (PRD §5.3).
 *
 * Sent as the `X-Nook-Surface` header rather than as a payload property: the
 * WhatsApp and MCP callers (blocks 24-28) reach `captureService.create`
 * directly with no HTTP request of their own, so the surface is an argument to
 * the service and a header at the HTTP edge, not part of the capture itself.
 */
export const captureSurfaceSchema = z.enum([
    "extension",
    "webapp",
    "mobile",
    "whatsapp",
    "mcp",
]);
/**
 * A trimmed, optional string. Whitespace-only collapses to `undefined`, so
 * `{"text":"   "}` is an empty capture rather than a nook with a blank body.
 */
const trimmed = (max) => z
    .string()
    .trim()
    .max(max)
    .optional()
    // Only spaces and invisible format characters (zero-width space, joiners, BOM) is no text at all.
    .transform((value) => (value && /[^\s\p{Cf}]/u.test(value) ? value : undefined));
/**
 * A client's own parse of the page it is saving (D7, block 08).
 *
 * Block 07 deliberately left this out — "a field with no producer is a guess".
 * It lands here because block 08 is its *consumer*: the confidence bar, the
 * borderline comparison and the tie-breakers are all server logic, and they
 * cannot be written against a shape that does not exist. Block 09's content
 * script is then the producer and adds no schema change of its own.
 *
 * There is no `wordCount` property on purpose. The bar counts the content it
 * was handed rather than believing a number, for the same reason `type` and
 * `status` are derived server-side: a client that could report its own word
 * count could decide whether its own capture gets fetched.
 */
export const extractedSchema = z.object({
    title: trimmed(512),
    /** Markdown. Required — an `extracted` with no content is a missing one. */
    content: z.string().trim().min(1).max(500_000),
    thumbnailUrl: z.url().max(2048).optional(),
    siteName: trimmed(200),
});
/** A highlight's color (block 45). A name, not a hex: each surface picks its own shade. */
export const highlightColorSchema = z.enum(["yellow", "green", "blue", "pink"]);
/**
 * Where a highlight sits on its page (block 45): the W3C TextQuote context and
 * TextPosition hint, in page text with all whitespace removed. The quote itself
 * is the nook's `content`.
 */
export const highlightAnchorSchema = z.object({
    prefix: z.string().max(64),
    suffix: z.string().max(64),
    start: z.int().nonnegative(),
    end: z.int().nonnegative(),
});
/**
 * `POST /v1/nooks` — **one object of optional properties, not a discriminated
 * union** (override 1, decision register D4).
 *
 * A union keys on one discriminator and therefore cannot express *a URL and an
 * image on one nook*, which PRD §5.1 promises and block 07's modal already
 * specs. Properties compose; variants do not. This is also why blocks 06, 07,
 * 09, 11 and 12 each *enable* a property rather than adding a variant.
 *
 * `text` and `highlightText` are separate fields carrying the same kind of
 * value on purpose (D5): a highlight is `{url, highlightText}` and a
 * URL-with-note is `{url, note}`, structurally similar and semantically
 * opposite. Which field carries the text is what decides whether the URL is
 * fetched — never a client-supplied flag.
 *
 * The iOS share extension builds this body by hand
 * (`apps/mobile/targets/share/ShareViewController.swift`): a change here goes there too.
 */
export const captureSchema = z
    .object({
    /** Triggers extraction, unless `highlightText` is set. Block 07. */
    url: z.url().max(2048).optional(),
    /**
     * A plain note's body. Becomes `nooks.user_note`, not `content` (block 47):
     * a note is the user's own text, all of it editable.
     */
    text: trimmed(100_000),
    /** A selection. Becomes `nooks.content` and suppresses the fetch. Block 11. */
    highlightText: trimmed(100_000),
    /**
     * An uploaded image or screenshot, at the path `POST /v1/uploads/sign`
     * returned. Enabled in block 06; block 12 adds `url` alongside it.
     *
     * Trusted only as far as its first path segment, which the capture service
     * checks against the caller — the backend signs downloads for whatever this
     * names, with a service-role key that bucket RLS does not apply to.
     */
    storagePath: trimmed(1024),
    /** The user's annotation. Becomes `nooks.user_note`. */
    note: trimmed(10_000),
    /** Source title when the client already knows it. Block 07. */
    pageTitle: trimmed(512),
    /**
     * The client's own parse of `url`, from a surface that can see a rendered
     * DOM the server never will (block 09). Trusted when it clears the
     * confidence bar, compared against a server parse when it does not (D7).
     */
    extracted: extractedSchema.optional(),
    /** Block 45. Only with `highlightText`; defaults to yellow. */
    highlightColor: highlightColorSchema.optional(),
    /** Block 45. Only with `highlightText`. */
    highlightAnchor: highlightAnchorSchema.optional(),
})
    .refine((p) => Boolean(p.url ?? p.text ?? p.highlightText ?? p.storagePath), {
    error: "at least one of url, text, highlightText or storagePath is required",
})
    .refine((p) => p.highlightText || !(p.highlightColor || p.highlightAnchor), {
    error: "highlightColor and highlightAnchor require highlightText",
});
/**
 * `nooks.type` records **only the two capture modes that change processing** —
 * the cases where content arrived with the capture and `source_url` must never
 * be fetched (override 2, D3). Everything else is an ordinary capture and is
 * `null`, described by which properties are present.
 */
export const nookTypeSchema = z.enum(["highlight", "screenshot"]);
export const nookStatusSchema = z.enum(["processing", "ready", "failed"]);
/** What the extraction router writes to `nooks.platform` (D3). */
export const nookPlatformSchema = z.enum(["youtube", "x", "linkedin", "reddit", "instagram", "tiktok", "amazon", "facebook", "threads", "substack"]);
/** A page, or a platform whose content is a whole Markdown article: read, excerpted and shown as one. `nook-service`'s `isArticle` is its SQL. */
export const readsAsArticle = (platform) => !platform || platform === "substack";
/**
 * `GET /v1/nooks` filters (block 19). AND across keys, OR within one. Absent
 * keys filter nothing, so `{}` is block 03's list exactly. `has` reads column
 * presence — `image` matches uploads and screenshots alike. Bounds carry an
 * offset because "a day" is the caller's day, not UTC's.
 */
/** `Note`: a nook with neither a URL nor an image — the Notes tab. `Capture`: every other nook — the Library. */
export var NookKind;
(function (NookKind) {
    NookKind["Note"] = "note";
    NookKind["Capture"] = "capture";
})(NookKind || (NookKind = {}));
export const nookFiltersSchema = z.object({
    source: z.array(captureSurfaceSchema).optional(),
    type: z.array(nookTypeSchema).optional(),
    has: z.array(z.enum(["url", "image", "note"])).optional(),
    kind: z.enum(NookKind).optional(),
    platform: z.array(nookPlatformSchema).optional(),
    /** Hosts without `www.`, as `GET /v1/nooks/sites` lists them. */
    site: z.array(z.string().max(253)).optional(),
    from: z.iso.datetime({ offset: true }).optional(),
    to: z.iso.datetime({ offset: true }).optional(),
    /** Nooks of one page, matched on its normalized URL (block 44). */
    url: z.string().max(2048).optional(),
});
/**
 * A row as the library list renders it.
 *
 * `excerpt` rather than `content`: from block 08 a nook's content is a whole
 * article, and a list of fifty of them is a payload nobody reads. The detail
 * response carries the full text.
 */
export const nookSummarySchema = z.object({
    id: z.uuid(),
    type: nookTypeSchema.nullable(),
    sourceUrl: z.string().nullable(),
    sourceTitle: z.string().nullable(),
    platform: z.string().nullable(),
    /** The source site's favicon as a `data:` URL, or `null`. */
    sourceIcon: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    /**
     * A signed URL for the nook's uploaded image, or `null` (block 06).
     *
     * Derived per response and never a column — `storage_path` is the fact, this
     * is a one-hour lease on it. It is on the *summary* as well as the detail
     * because the library grid renders thumbnails, and the backend batch-signs a
     * page in one call rather than the client signing fifty.
     */
    imageUrl: z.string().nullable(),
    excerpt: z.string().nullable(),
    userNote: z.string().nullable(),
    summary: z.string().nullable(),
    status: nookStatusSchema,
    capturedAt: z.iso.datetime(),
    /** The last `userNote` edit, or `capturedAt`. The Notes tab sorts by it (block 47). */
    editedAt: z.iso.datetime(),
    /**
     * No URL and no image (block 47). Server-derived, so every client asks the
     * same question; a note captured before 47 is one too, with its body in `content`.
     */
    isNote: z.boolean(),
    /** Block 45. Set on every highlight, `null` on everything else. */
    highlightColor: highlightColorSchema.nullable(),
    /** Block 45. On the summary because the extension repaints from the list. */
    highlightAnchor: highlightAnchorSchema.nullable(),
});
/**
 * `GET /v1/nooks/:id`.
 *
 * `platformMetadata` is never returned raw (A3 Q2); `media` is its typed
 * projection. `extractionError` gained its producer in block 08 and so gained
 * its property — additive, as that note promised.
 *
 * It is on the **detail** only. The card renders a failure from `status`
 * alone, so putting the reason on the summary would ship fifty error strings
 * per page for a line nothing prints.
 */
export const nookSchema = nookSummarySchema.extend({
    content: z.string().nullable(),
    /**
     * A `generate-summary` job is on the queue for this nook (block 14).
     *
     * Derived per response from the queue itself and never a column: it answers
     * "is one in flight", and a column would be a second copy of that fact to
     * keep in step with pgmq. Clients poll the detail while it is `true`.
     *
     * Detail only, like `extractionError` — the list does not report it.
     */
    summaryPending: z.boolean(),
    /**
     * The server would accept a request to summarise this nook: it is not
     * `processing` and has enough `content` (block 14 override 14). Gates the
     * webapp's "Generate summary" button, so the length rule stays server-side.
     */
    summarizable: z.boolean(),
    /** `FETCH_FAILED`, `TIMEOUT`, `UNSAFE_URL`, … — never user-facing copy. */
    extractionError: z.string().nullable(),
    storagePath: z.string().nullable(),
    /**
     * What a platform pipeline recovered, camelCase and client-safe (block 22a).
     * `null` for anything that did not come through one. The rest is YouTube's
     * (block 21): `resumeAt` is the `t` the saved URL already carries, in seconds,
     * and `transcript` is its segments joined into lines of about 30 seconds.
     */
    media: z
        .object({
        author: z.string().optional(),
        durationSeconds: z.number().optional(),
        publishedAt: z.string().optional(),
        resumeAt: z.number().optional(),
        transcriptUnavailable: z.boolean().optional(),
        transcript: z.array(z.object({ start: z.number(), text: z.string() })).optional(),
        /** What the detail panel renders in the platform's own look (block 43). */
        embed: nookEmbedSchema.optional(),
    })
        .nullable(),
    captureSurface: captureSurfaceSchema,
    updatedAt: z.iso.datetime(),
});
/** `GET /v1/nooks`. Cursor is opaque — clients echo it, never parse it. */
export const nookListSchema = z.object({
    items: z.array(nookSummarySchema),
    nextCursor: z.string().nullable(),
});
/** `GET /v1/nooks/sites`: every host this user has saved from, A–Z. */
export const nookSiteListSchema = z.object({
    items: z.array(z.object({ host: z.string(), icon: z.string().nullable() })),
});
/**
 * `201` from `POST /v1/nooks`. Both values are **derived server-side from the
 * payload** — `ready` unless a `url` or `storagePath` needs work first — so no
 * caller can pass a flag that changes how its capture is processed.
 */
export const captureResultSchema = z.object({
    id: z.uuid(),
    status: z.enum(["ready", "processing"]),
});
/**
 * `PATCH /v1/nooks/:id` (block 37).
 *
 * **The strictness is the immutability rule.** PRD §4.2 makes `user_note` the
 * only mutable part of a nook, and this object enforces that by accepting
 * exactly one key rather than by a deny-list of column names in the route:
 * `content`, `sourceUrl`, `sourceTitle`, `capturedAt` and `summary` are
 * rejected as unrecognized keys, which the route turns into `immutable_field`.
 * A future mutable field is added *here*; nothing is removed from a list
 * somewhere else. Block 45 added `highlightColor`, accepted on highlights only.
 *
 * `userNote`, not `user_note` — camelCase on the wire, like every other body
 * (D6). Empty and whitespace-only collapse to `null` rather than to `''`, so
 * clearing a note leaves the same value as never having written one: a card
 * with no snippet, and no lone space in the FTS index.
 */
export const updateNookSchema = z
    .strictObject({
    userNote: z
        .string()
        .trim()
        // A note's whole body lives here (block 47): capture joins `text` and `note`, so it takes both.
        .max(110_002)
        .nullable()
        .transform((value) => value || null)
        .optional(),
    highlightColor: highlightColorSchema.optional(),
})
    .refine((p) => p.userNote !== undefined || p.highlightColor, {
    error: "userNote or highlightColor is required",
});
/**
 * `POST /v1/nooks/:id/summary/regenerate` (block 14).
 *
 * `202` and a flag, never the text: the summary is written by a worker, so the
 * only honest answer at the end of the request is that one is coming.
 *
 * `z.boolean()` rather than `z.literal(true)` — a shape that can only ever say
 * one thing is a shape a client cannot branch on the day it says the other.
 */
export const summaryStatusSchema = z.object({ summaryPending: z.boolean() });
//# sourceMappingURL=nook.js.map